import { createFileRoute, Link } from "@tanstack/react-router";
import { Icon } from "@/components/Icon";

import { Button } from "@/components/ui/button";
import { FaqList } from "@/components/FaqAccordion";
import { Reveal } from "@/components/Reveal";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { AGENCY } from "@/lib/agency";
import { buildHead } from "@/lib/seo";
import { serviceAreas } from "@/lib/service-areas";

const HERO_IMG =
  "https://images.unsplash.com/photo-1545063328-c8e3faffa16f?auto=format&fit=crop&w=1600&q=80";

const trustPoints = [
  { icon: "clock", t: "Decisions in 1 hour" },
  { icon: "handcoins", t: "$0 upfront fees" },
  { icon: "shieldcheck", t: "Texas-based advisors" },
];

const differentiators = [
  {
    icon: "compass",
    title: "We know the DFW operating climate",
    body: "From Stemmons Corridor warehouses to Plano medical offices and Frisco retail, we know the cost structures, lease norms, and licensing realities lenders ask about before they fund a Dallas-area business.",
  },
  {
    icon: "users",
    title: "One advisor, not a call-center queue",
    body: "A national online portal hands your file to whoever picks up. Here, the same advisor takes the first call, packages the file, negotiates terms, and stays with you through funding, across every city we cover.",
  },
  {
    icon: "handcoins",
    title: "Lender relationships built for Texas borrowers",
    body: "Our lender panel includes SBA preferred lenders, regional Texas banks, equipment specialists, and revenue-based funders that already underwrite DFW businesses every week, so files don't stall on unfamiliar geography.",
  },
  {
    icon: "building2",
    title: "Coverage without a branch overhead",
    body: "We serve businesses across the metro from our Dallas office. No drive to a branch, no appointment windows. Most files move from intro call to term sheet without an in-person meeting.",
  },
];

const steps = [
  {
    title: "Tell us where you operate",
    body: "Share your city, industry, and what the capital is for. We map your profile to the lenders most active in that part of the metro.",
  },
  {
    title: "We package the file once",
    body: "One intake, one document set. Your advisor structures the request the way each lender underwrites it. No re-explaining your business to five people.",
  },
  {
    title: "Compare real offers",
    body: "You see actual term sheets (amount, term length, payment, fees) side by side. Nothing is binding until you sign.",
  },
  {
    title: "Fund and move on",
    body: "Funds land in your business account, often within days. Your advisor stays available for renewals, draws, or follow-on programs.",
  },
];

const faqs = [
  {
    q: "Do I have to be in Dallas proper to work with you?",
    a: "No. We arrange financing for businesses throughout the Dallas-Fort Worth metroplex and the surrounding suburbs listed on this page. If your business is licensed and operating in Texas, we can almost always help, even if your city isn't listed individually.",
  },
  {
    q: "Do you have a physical office in every city you serve?",
    a: `Our office is in Dallas at ${AGENCY.address}. We serve the rest of the metro remotely by phone and secure document upload, which is how most owners prefer to handle financing anyway. We don't claim branches in cities where we don't operate.`,
  },
  {
    q: "Does the city my business is in affect approval or rates?",
    a: "Approval and pricing are driven by your revenue, time in business, credit, and industry, not your ZIP code inside the metro. Where city does matter is documentation: local licensing, sales tax permits, and lease paperwork. We handle that during packaging so it doesn't slow the file.",
  },
  {
    q: "What if my business operates in multiple cities?",
    a: "Common in DFW. We package the file around your primary operating entity and document the additional locations as part of the request. Multi-location businesses often qualify for larger lines of credit and SBA structures than single-location operators.",
  },
  {
    q: "Can you help if I'm just outside the listed suburbs?",
    a: "Yes. The list reflects the suburbs we serve most often, but coverage extends across the broader DFW metro. Call us and we'll confirm fit on the first conversation. There's no charge for the intake.",
  },
];

export const Route = createFileRoute("/service-areas/")({
  head: () => {
    const head = buildHead({
      title: `Service Areas | ${AGENCY.name}`,
      description:
        "Business loan advisory serving the Dallas-Fort Worth metroplex. See every city and suburb we cover across DFW, and how local service works without a branch in every town.",
      path: "/service-areas",
      image: HERO_IMG,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Service Areas", path: "/service-areas" },
      ],
      faqs: faqs.map((f) => ({ q: f.q, a: f.a })),
    });
    // Preload the eager above-the-fold hero so the browser fetches the LCP
    // image immediately instead of after HTML parse/hydration.
    return {
      ...head,
      links: [
        ...head.links,
        { rel: "preload", as: "image", href: HERO_IMG, fetchPriority: "high" },
      ],
    };
  },
  component: ServiceAreasPage,
});

function ServiceAreasPage() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              "radial-gradient(ellipse at 85% 0%, oklch(0.42 0.16 250) 0%, transparent 55%), radial-gradient(ellipse at 0% 100%, oklch(0.3 0.1 260) 0%, transparent 50%)",
          }}
        />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-6 py-20 md:py-24 lg:grid-cols-[1.1fr_1fr] lg:items-center lg:px-10">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-primary-foreground/85">
              <Icon name="mappin" color="bl" className="h-3.5 w-3.5 text-accent brightness-0 invert"  />
              Dallas-Fort Worth Metroplex
            </p>
            <h1 className="mt-6 max-w-2xl font-serif text-5xl font-semibold leading-[1.05] tracking-tight md:text-6xl">
              Business loan advisors serving every corner of DFW.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-primary-foreground/85">
              From Dallas and Fort Worth to the suburbs in between, {AGENCY.name}{" "}
              arranges working capital, SBA loans, lines of credit, and
              equipment financing for owners across the metro. Same advisor,
              same process, wherever your business operates.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
              >
                <a href={AGENCY.phoneHref}>
                  <Icon name="phonecall" color="bl" className="h-4 w-4 brightness-0 invert"  /> Call {AGENCY.phone}
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10"
              >
                <Link to="/apply-funding">Apply Now</Link>
              </Button>
            </div>
            <ul className="mt-8 grid max-w-xl gap-2 text-sm text-primary-foreground/90 sm:grid-cols-3">
              {trustPoints.map(({ icon: __iconName, t }) => (
                <li key={t} className="flex items-center gap-2">
                  <Icon name={__iconName} color="co" className="h-4 w-4 text-accent" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <Reveal className="relative">
            <div className="overflow-hidden rounded-2xl border border-primary-foreground/15 shadow-2xl">
              <img
                src={HERO_IMG}
                alt="Aerial view of business districts across the Dallas-Fort Worth metroplex at dusk"
                className="h-[420px] w-full object-cover"
                loading="eager"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 hidden rounded-xl border border-border bg-background p-4 text-foreground shadow-lg md:block">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-accent">
                Coverage
              </p>
              <p className="mt-1 font-serif text-2xl font-semibold">
                {serviceAreas.length}+ cities
              </p>
              <p className="text-xs text-muted-foreground">
                across the DFW metroplex
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Areas served */}
      <section className="border-b border-border bg-background">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
                Areas served
              </p>
              <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight md:text-4xl">
                Cities and suburbs we cover
              </h2>
              <p className="mt-4 text-muted-foreground">
                Each city links to local context for owners financing growth in
                that part of the metro. Don't see yours? Call us. Coverage
                extends across the wider DFW area.
              </p>
            </div>
          </div>

          <ul className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {serviceAreas.map((a, i) => (
              <Reveal as="li" key={a.slug} delay={i * 30}>
                <a
                  href={`/service-areas/${a.slug}`}
                  className="group flex h-full items-start justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent hover:shadow-sm"
                >
                  <div>
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-accent">
                      <Icon name="mappin" color="bl" className="h-3.5 w-3.5"  />
                      Service area
                    </div>
                    <p className="mt-2 font-serif text-lg font-semibold leading-snug">
                      {a.city}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Business loans &amp; advisory
                    </p>
                  </div>
                  <Icon name="chevronright" color="bl" className="mt-1 h-4 w-4 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-accent"  />
                </a>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* How local service works */}
      <section className="border-b border-border bg-secondary/40">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
                How local service works
              </p>
              <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight md:text-4xl">
                Same advisor. Same process. Wherever your business is.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-muted-foreground">
                Whether you're running a restaurant in Bishop Arts, a clinic in
                Plano, a contractor in Arlington, or an e-commerce warehouse in
                Frisco, the process to arrange financing is the same. You work
                with one Dallas-based advisor from intake through funding. No
                handoffs, no call-center queue, no re-explaining your business
                to a new person every week.
              </p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                We don't operate branches in every suburb, and we don't pretend
                to. What we do is meet owners where they actually are: on the
                phone and on a shared document folder, usually
                without ever asking you to leave the shop.
              </p>
            </div>
            <ol className="grid gap-4 sm:grid-cols-2">
              {steps.map((s, i) => (
                <Reveal as="li" key={s.title} delay={i * 60}>
                  <div className="h-full rounded-2xl border border-border bg-background p-6">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent/10 font-serif text-sm font-semibold text-accent">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <h3 className="mt-4 font-serif text-lg font-semibold">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {s.body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Why local-focused */}
      <section className="border-b border-border bg-background">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
              Why work with us
            </p>
            <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight md:text-4xl">
              A local-focused advisor beats a national portal.
            </h2>
            <p className="mt-4 text-muted-foreground">
              National online lenders treat every business the same. We don't,
              because Dallas-area owners aren't the same as operators in
              Phoenix or Tampa, and lenders price the difference.
            </p>
          </div>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {differentiators.map(({ icon: __iconName, title, body }, i) => (
              <Reveal key={title} delay={i * 60}>
                <article className="h-full rounded-2xl border border-border bg-card p-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent">
                    <Icon name={__iconName} color="co" className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-serif text-lg font-semibold">
                    {title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {body}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-border bg-secondary/40">
        <div className="mx-auto max-w-4xl px-6 py-20 lg:px-10">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            FAQ
          </p>
          <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            Coverage and funding questions
          </h2>
          <FaqList faqs={faqs} serif />
        </div>
      </section>

      {/* Final CTA */}
      <section id="apply" className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-5xl px-6 py-20 text-center lg:px-10">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Ready when you are
          </p>
          <h2 className="mt-4 font-serif text-4xl font-semibold leading-tight md:text-5xl">
            Talk to a Dallas advisor today.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-primary-foreground/85">
            One call, one advisor, no upfront fees. We'll tell you on the first
            conversation whether we can help and which programs to compare
            for your city, industry, and revenue.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
            >
              <a href={AGENCY.phoneHref}>
                <Icon name="phonecall" color="bl" className="h-4 w-4 brightness-0 invert"  /> Call {AGENCY.phone}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10"
            >
              <Link to="/apply-funding">Apply Now</Link>
            </Button>
          </div>
          <p className="mt-6 text-xs text-primary-foreground/60">
            {AGENCY.hours} · {AGENCY.address}
          </p>
        </div>
      </section>

      <MobileStickyBar applyHref="/apply-funding" />
    </>
  );
}
